import { Injectable } from '@angular/core';
import { ReportTypes, SubscriptionPlans } from './user/user.model';
import { MenuGroupType } from '../map/menu/menu-groups';
import { IntegrationsService } from '../shared/util/integrations';

@Injectable({
  providedIn: 'root',
})
export class GoogleAnalyticsService {
  constructor(private integrations: IntegrationsService) {}

  public logChooseCoverageArea(
    plan: SubscriptionPlans,
    promoCode: string | null
  ) {
    this.integrations.track(
      'choose_coverage_area',
      {
        subscription_plan: plan,
        subscription_promo_code: promoCode,
      },
      { ga: true, amplitude: true }
    );
  }

  public logSubscriptionGetStarted(
    plan: SubscriptionPlans,
    monthly: boolean,
    promoCode: string | null
  ) {
    this.integrations.track(
      'SUBSCRIPTION_GET_STARTED',
      {
        subscription_plan: plan,
        subscription_period: monthly ? 'MONTHLY' : 'YEARLY',
        subscription_promo_code: promoCode,
      },
      { ga: true, amplitude: true }
    ); // Track in amplitude too
  }

  public logSubscriptionToPayment(
    plan: SubscriptionPlans,
    monthly: boolean,
    promoCode: string | null
  ) {
    this.integrations.track(
      'SUBSCRIPTION_TO_PAYMENT',
      {
        subscription_plan: plan,
        subscription_period: monthly ? 'MONTHLY' : 'YEARLY',
        subscription_promo_code: promoCode,
      },
      { ga: true, amplitude: true }
    );
  }

  public logSeeAllIndicators(plan: SubscriptionPlans) {
    this.integrations.track(
      'PROFILE_SUBSCRIPTION_ALL_INDICATORS',
      {
        subscription_plan: plan,
      },
      { ga: true, amplitude: true }
    );
  }

  /**
   * A sample-PDF click on /pricing's report cards. Deliberately not
   * `logSampleReportClicked` below: that event's only dimension is
   * `subscription_plan`, and feeding it a `ReportTypes` value would put
   * 'COMMUNITY_COMPASS' where every existing report and audience segment
   * expects a plan id.
   */
  public logReportSampleClicked(reportType: ReportTypes) {
    this.integrations.track(
      'report_sample_clicked',
      {
        report_type: reportType,
      },
      { ga: true, amplitude: true }
    );
  }

  public logSampleReportClicked(plan: SubscriptionPlans) {
    this.integrations.track(
      'PROFILE_SUBSCRIPTION_SAMPLE_REPORT',
      {
        subscription_plan: plan,
      },
      { ga: true, amplitude: true }
    );
  }

  public logToggleSubscriptionPeriod(monthly: boolean) {
    this.integrations.track(
      'PROFILE_SUBSCRIPTION_PERIOD_TOGGLE',
      {
        subscription_period: monthly ? 'MONTHLY' : 'YEARLY',
      },
      { ga: true, amplitude: true }
    );
  }

  public logClicksReachedLimit() {
    this.integrations.track(
      'FREE_CLICKS_LIMIT_REACHED',
      {},
      {
        ga: true,
        amplitude: true,
      }
    );
  }

  public logReportOrderTypeConfirmed() {
    this.integrations.track(
      'ORDER_REPORT_CONFIRM_TYPE',
      {},
      {
        ga: true,
        amplitude: true,
      }
    );
  }

  public logReportOrderLocationConfirmed() {
    this.integrations.track(
      'ORDER_REPORT_CONFIRM_LOCATION',
      {},
      {
        ga: true,
        amplitude: true,
      }
    );
  }

  public logFreeReportOrder() {
    this.integrations.track(
      'FREE_REPORT_ORDER',
      {},
      { ga: true, amplitude: true }
    );
  }

  public logCompareLocationsClick(from: 'map' | 'compare-page') {
    this.integrations.track(
      'COMPARE_CLICK',
      { compare_source: from },
      { ga: true, amplitude: true }
    );
  }

  public logMenuGroupChanged(group: MenuGroupType) {
    this.integrations.track(
      'MENU_GROUP_CHANGED',
      { menu_group: group },
      { ga: true, amplitude: true }
    );
  }

  public logSignInGoogle() {
    this.integrations.track(
      'sign_in_google',
      {},
      { ga: true, amplitude: true }
    );
  }

  public logSignInEmail() {
    this.integrations.track('sign_in_email', {}, { ga: true, amplitude: true });
  }

  public logLockedParamClickGuest() {
    this.integrations.track(
      'locked_param_click_guest',
      {},
      { ga: true, amplitude: true }
    );
  }

  public logStartFreeTrialClick() {
    this.integrations.track(
      'start_free_trial_click',
      {},
      { ga: true, amplitude: true }
    );
  }

  public logFormStart() {
    this.integrations.track('form_start', {}, { ga: true, amplitude: true });
  }

  public logCreateAccountClick() {
    this.integrations.track(
      'create_account_click',
      {},
      { ga: true, amplitude: true }
    );
  }

  public logLockedParamClickRegistered() {
    this.integrations.track(
      'locked_param_click_registered',
      {},
      { ga: true, amplitude: true }
    );
  }

  public logGetStartedLockedParam() {
    this.integrations.track(
      'get_started_locked_param',
      {},
      { ga: true, amplitude: true }
    );
  }

  public logGetStartedPaywall(
    plan: SubscriptionPlans,
    monthly: boolean,
    promoCode: string | null
  ) {
    this.integrations.track(
      'get_started_paywall',
      {
        subscription_plan: plan,
        subscription_period: monthly ? 'MONTHLY' : 'YEARLY',
        subscription_promo_code: promoCode,
      },
      { ga: true, amplitude: true }
    );
  }

  public logGetStartedProfile(
    plan: SubscriptionPlans,
    monthly: boolean,
    promoCode: string | null
  ) {
    this.integrations.track(
      'get_started_profile',
      {
        subscription_plan: plan,
        subscription_period: monthly ? 'MONTHLY' : 'YEARLY',
        subscription_promo_code: promoCode,
      },
      { ga: true, amplitude: true }
    );
  }

  public logGuideUserCategory(category: string) {
    this.integrations.track(
      'guide_user_category',
      { user_category: category },
      { ga: true, amplitude: true }
    );
  }

  public logUserChangedCategory(category: string) {
    this.integrations.track(
      'user_changed_category',
      { user_category: category },
      { ga: true, amplitude: true }
    );
  }

  public logSearchDone(source: 'suggestion' | 'history') {
    this.integrations.track(
      'map_search_done',
      { search_source: source },
      { ga: true, amplitude: true }
    );
  }

  public logBeginCheckout(
    plan: SubscriptionPlans,
    monthly: boolean,
    promoCode: string | null
  ) {
    this.integrations.track(
      'begin_checkout',
      {
        subscription_plan: plan,
        subscription_period: monthly ? 'MONTHLY' : 'YEARLY',
        subscription_promo_code: promoCode,
      },
      { ga: true, amplitude: true }
    );
  }

  // Sales Forecast fake-door funnel — these events are the go/no-go signal
  // for building the real forecast engine

  public logSalesForecastPageView(industry: string) {
    this.integrations.track(
      'sales_forecast_page_view',
      { sales_forecast_industry: industry },
      { ga: true, amplitude: true }
    );
  }

  public logSalesForecastAddressSelected(industry: string) {
    this.integrations.track(
      'sales_forecast_address_selected',
      { sales_forecast_industry: industry },
      { ga: true, amplitude: true }
    );
  }

  public logSalesForecastTeaserShown(industry: string) {
    this.integrations.track(
      'sales_forecast_teaser_shown',
      { sales_forecast_industry: industry },
      { ga: true, amplitude: true }
    );
  }

  public logSalesForecastComingSoonClick(industry: string) {
    this.integrations.track(
      'sales_forecast_coming_soon_click',
      { sales_forecast_industry: industry },
      { ga: true, amplitude: true }
    );
  }

  // Fired once per page visit, on the first edit of the customer profile
  // calculator — measures whether visitors engage with audience targeting
  public logSalesForecastProfileAdjusted(industry: string) {
    this.integrations.track(
      'sales_forecast_profile_adjusted',
      { sales_forecast_industry: industry },
      { ga: true, amplitude: true }
    );
  }

  // "Someone wants to convert" signal for a sales-forecast paywall/upgrade
  // prompt — the GA/Amplitude counterpart to SalesForecastFunnelService.
  // subscriptionClicked(), which logs the same click to our own backend.
  // Subscription-specific: do not fire this for a report-purchase click
  // (see logSalesForecastReportIntentClicked below) — mixing the two would
  // corrupt subscription-conversion analysis with unrelated clicks.
  public logSalesForecastSubscriptionClicked(placement: string) {
    this.integrations.track(
      'sales_forecast_subscription_clicked',
      { sales_forecast_placement: placement },
      { ga: true, amplitude: true }
    );
  }

  // An already-subscribed visitor clicked toward ordering the separate,
  // one-time paid report (e.g. the top-nav CTA) — a softer, earlier signal
  // than logSalesForecastCtaClicked below, which fires only once the real
  // Stripe checkout session is actually created in onCheckout(). Kept as
  // its own event rather than reusing either one: this is neither a
  // subscription action nor a confirmed checkout attempt.
  public logSalesForecastReportIntentClicked(placement: string) {
    this.integrations.track(
      'sales_forecast_report_intent_clicked',
      { sales_forecast_placement: placement },
      { ga: true, amplitude: true }
    );
  }

  public logSalesForecastCtaClicked(industry: string, priceUsd: number) {
    this.integrations.track(
      'sales_forecast_cta_clicked',
      {
        sales_forecast_industry: industry,
        sales_forecast_price_usd: priceUsd,
      },
      { ga: true, amplitude: true }
    );
  }

  public logSalesForecastCheckoutCompleted(industry: string, priceUsd: number) {
    this.integrations.track(
      'sales_forecast_checkout_completed',
      {
        sales_forecast_industry: industry,
        sales_forecast_price_usd: priceUsd,
      },
      { ga: true, amplitude: true }
    );
  }
}
